import { Box, Typography } from "@mui/material";
import { LoadingButton } from "@aclymatepackages/atoms";

// Each gap kind has a different cause and a different remedy, so they get
// different copy: one never started, one stopped, and one is still delivering
// for every account except the ones the bank no longer shares.
const DATA_GAP_EXPLANATIONS = {
  neverDelivered: (institutionName) =>
    `This connection is authorized, but ${institutionName} has never sent us a transaction. That usually means no eligible accounts were selected when it was linked — check the accounts below, then reconnect ${institutionName} and be sure to select the accounts you want imported.`,
  // Deliberately does not claim the account was still delivering -- that is
  // true for a de-selected account but not for one that never got the chance
  // to send anything before it was removed, and this copy covers both.
  accountsRemoved: (institutionName) =>
    `${institutionName} is connected, but it has stopped sharing at least one account we don't have complete data for — most often because that account was de-selected the last time this connection was reconnected. The accounts still being shared are listed below. To start importing the missing one, reconnect ${institutionName} and select every account you want included. Transactions already imported are still counted in your footprint.`,
  stale: (institutionName) =>
    `This connection is still authorized, but ${institutionName} has not sent us any new transactions. Try "Refresh transactions" first. If nothing arrives, reconnect ${institutionName} specifically — reconnecting your other institutions will not restore this one.`
};

export const dataGapExplanation = (level, institutionName) =>
  (DATA_GAP_EXPLANATIONS[level] || DATA_GAP_EXPLANATIONS.stale)(
    institutionName
  );

// A data gap is not an error (doesRequireReauthentication is false), so the
// "Reauthenticate" button in the connection-error popup never renders for
// this case -- every gap explanation's remedy is "reconnect", so this needs
// its own action instead of relying on that other button.
//
// canEdit gates this the same way the existing connect/add controls
// elsewhere on this card already do -- a read-only viewer must not be able
// to launch update-mode Plaid and change which accounts are shared. This is
// belt-and-suspenders with the server-side role check in
// functions/controllers/integrations.js, which is the actual enforcement
// boundary; hiding the button is a UX nicety, not the security control.
const DataGapWarning = ({
  level,
  label,
  institutionName,
  onReconnect,
  isLoading,
  canEdit
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" color="warning.main">
      {label}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {dataGapExplanation(level, institutionName)}
    </Typography>
    {canEdit && (
      <LoadingButton
        isLoading={isLoading}
        onClick={onReconnect}
        color="primary"
        variant="outlined"
        label="Reconnect"
      />
    )}
  </Box>
);

export default DataGapWarning;
